import { Vector3 } from "three";

/**
 * Find the closest points to a given point on the same axis (x or y)
 * Picks only ONE closest point from each axis (X and Y), maximum 2 points total
 * @param {Object|Vector3} currentPoint - The reference point (can be {x, y, z} or Vector3)
 * @param {Array<Object>} availablePoints - Array of available nova points with {id, x, y, z, point}
 * @param {number} maxPoints - Maximum number of points to return (default: 3, but will return max 2)
 * @param {number} axisTolerance - Tolerance for considering points on the same axis (default: 10)
 * @returns {Array<Object>} Array of closest point objects - one from each axis (max 2)
 */
export const findClosestPoints = (currentPoint, availablePoints, maxPoints = 3, axisTolerance = 10) => {
  if (!currentPoint || !availablePoints || availablePoints.length === 0) {
    return [];
  }

  // Separate points into X-axis aligned and Y-axis aligned
  const xAxisPoints = [];
  const yAxisPoints = [];

  availablePoints.forEach(item => {
    const dx = Math.abs(item.x - currentPoint.x);
    const dy = Math.abs(item.y - currentPoint.y);

    // Check if point is on the same X-axis (vertical alignment - same x, different y)
    if (dx <= axisTolerance) {
      xAxisPoints.push({
        ...item,
        distance: dy, // Distance along Y-axis
        axis: 'x'
      });
    }

    // Check if point is on the same Y-axis (horizontal alignment - same y, different x)
    if (dy <= axisTolerance) {
      yAxisPoints.push({
        ...item,
        distance: dx, // Distance along X-axis
        axis: 'y'
      });
    }
  });

  // Sort both arrays by distance
  xAxisPoints.sort((a, b) => a.distance - b.distance);
  yAxisPoints.sort((a, b) => a.distance - b.distance);

  // Pick ONLY the closest point from each axis
  const result = [];

  // Add the closest X-axis point (if any)
  if (xAxisPoints.length > 0) {
    result.push(xAxisPoints[0]);
  }

  // Add the closest Y-axis point (if any and not the same as X-axis point)
  if (yAxisPoints.length > 0) {
    // Make sure we don't add the same point twice
    if (result.length === 0 || result[0].id !== yAxisPoints[0].id) {
      result.push(yAxisPoints[0]);
    }
  }

  return result;
};

/**
 * Find the top-left point (minimum x and maximum y) from an array of points
 * @param {Array<Object>} points - Array of point objects with {id, x, y, z, point}
 * @returns {Object|null} The top-left point object or null if no points
 */
export const findTopLeftPoint = (points) => {
  if (!points || points.length === 0) {
    return null;
  }

  // Find minimum x and maximum y values across all points
  const minX = Math.min(...points.map(p => p.x));
  const maxY = Math.max(...points.map(p => p.y));

  // Find the point closest to (minX, maxY)
  // This handles edge cases where no single point has both absolute min x and max y
  let closestPoint = points[0];
  let minDistance = Math.sqrt(
    Math.pow(points[0].x - minX, 2) + Math.pow(points[0].y - maxY, 2)
  );

  for (let i = 1; i < points.length; i++) {
    const distance = Math.sqrt(
      Math.pow(points[i].x - minX, 2) + Math.pow(points[i].y - maxY, 2)
    );

    if (distance < minDistance) {
      minDistance = distance;
      closestPoint = points[i];
    } else if (distance === minDistance) {
      // In case of tie, prefer lower x, then higher y (lexicographic order)
      if (points[i].x < closestPoint.x ||
          (points[i].x === closestPoint.x && points[i].y > closestPoint.y)) {
        closestPoint = points[i];
      }
    }
  }

  return closestPoint;
};

/**
 * Remove a point from an array of points by ID
 * @param {Array<Object>} points - Array of point objects
 * @param {number} pointId - ID of point to remove
 * @returns {Array<Object>} New array without the removed point
 */
export const removePointById = (points, pointId) => {
  return points.filter(item => item.id !== pointId);
};

/**
 * Check if a point ID is already in a line's endpoints
 * @param {Array} lines - Array of lines
 * @param {number} pointId - Point ID to check
 * @param {Array<Object>} allNovaPoints - All nova points to match coordinates
 * @returns {boolean} True if point is used in any line
 */
export const isPointUsedInLine = (lines, pointId, allNovaPoints) => {
  const pointObj = allNovaPoints.find(item => item.id === pointId);
  if (!pointObj) return false;

  return lines.some(line =>
    line.points.some(linePoint =>
      linePoint.x === pointObj.x &&
      linePoint.y === pointObj.y &&
      linePoint.z === pointObj.z
    )
  );
};
